// Parser for the FTMS Indoor Bike Data characteristic (0x2AD2).
// Spec: Bluetooth SIG Fitness Machine Service v1.0, §4.9.
//
// Packet layout: 16-bit flags at offset 0, then a variable-length payload of
// optional fields. Each field is present only when its flag bit is set, so
// downstream offsets shift depending on which earlier fields were included.
//
// Bit 0 has inverted semantics — instantaneous speed is present when bit 0
// is *clear*. Every other bit follows the normal "set = present" convention.
//
// DEFENSIVE: Real hardware truncates payloads. Every read is length-guarded.
// If a field's bytes aren't available, the field returns null instead of
// throwing — we keep parsing whatever later fields *do* fit.

import type { IndoorBikeMetrics } from "../types";

const FLAG_MORE_DATA = 0x0001;
const FLAG_AVG_SPEED = 0x0002;
const FLAG_INST_CADENCE = 0x0004;
const FLAG_AVG_CADENCE = 0x0008;
const FLAG_TOTAL_DISTANCE = 0x0010;
const FLAG_RESISTANCE = 0x0020;
const FLAG_INST_POWER = 0x0040;
const FLAG_AVG_POWER = 0x0080;
const FLAG_EXPENDED_ENERGY = 0x0100;
const FLAG_HEART_RATE = 0x0200;
const FLAG_METABOLIC_EQ = 0x0400;
const FLAG_ELAPSED_TIME = 0x0800;
const FLAG_REMAINING_TIME = 0x1000;

const fits = (view: DataView, offset: number, size: number): boolean =>
  offset + size <= view.byteLength;

export function parseIndoorBikeData(view: DataView): IndoorBikeMetrics {
  const out: IndoorBikeMetrics = {
    type: "indoor_bike",
    power_watts: null,
    heart_rate_bpm: null,
    elapsed_time_s: null,
    remaining_time_s: null,
    cadence_rpm: null,
    speed_kmh: null,
    avg_speed_kmh: null,
    avg_cadence_rpm: null,
    avg_power_watts: null,
    total_distance_m: null,
    resistance_level: null,
    total_energy_kcal: null,
  };

  if (view.byteLength < 2) return out;
  const flags = view.getUint16(0, true);
  let o = 2;

  if ((flags & FLAG_MORE_DATA) === 0) {
    if (fits(view, o, 2)) {
      out.speed_kmh = view.getUint16(o, true) * 0.01;
      o += 2;
    }
  }
  if (flags & FLAG_AVG_SPEED) {
    if (fits(view, o, 2)) {
      out.avg_speed_kmh = view.getUint16(o, true) * 0.01;
      o += 2;
    }
  }
  if (flags & FLAG_INST_CADENCE) {
    if (fits(view, o, 2)) {
      out.cadence_rpm = view.getUint16(o, true) * 0.5;
      o += 2;
    }
  }
  if (flags & FLAG_AVG_CADENCE) {
    if (fits(view, o, 2)) {
      out.avg_cadence_rpm = view.getUint16(o, true) * 0.5;
      o += 2;
    }
  }
  if (flags & FLAG_TOTAL_DISTANCE) {
    if (fits(view, o, 3)) {
      const lo = view.getUint16(o, true);
      const hi = view.getUint8(o + 2);
      out.total_distance_m = lo | (hi << 16);
      o += 3;
    }
  }
  if (flags & FLAG_RESISTANCE) {
    if (fits(view, o, 2)) {
      out.resistance_level = view.getInt16(o, true);
      o += 2;
    }
  }
  if (flags & FLAG_INST_POWER) {
    if (fits(view, o, 2)) {
      out.power_watts = view.getInt16(o, true);
      o += 2;
    }
  }
  if (flags & FLAG_AVG_POWER) {
    if (fits(view, o, 2)) {
      out.avg_power_watts = view.getInt16(o, true);
      o += 2;
    }
  }
  if (flags & FLAG_EXPENDED_ENERGY) {
    // Total (uint16 kcal), per hour (uint16), per minute (uint8); 0xFFFF is
    // "not available".
    if (fits(view, o, 5)) {
      const total = view.getUint16(o, true);
      if (total !== 0xffff) out.total_energy_kcal = total;
      o += 5;
    }
  }
  if (flags & FLAG_HEART_RATE) {
    if (fits(view, o, 1)) {
      out.heart_rate_bpm = view.getUint8(o);
      o += 1;
    }
  }
  if (flags & FLAG_METABOLIC_EQ) {
    if (fits(view, o, 1)) {
      o += 1;
    }
  }
  if (flags & FLAG_ELAPSED_TIME) {
    if (fits(view, o, 2)) {
      out.elapsed_time_s = view.getUint16(o, true);
      o += 2;
    }
  }
  if (flags & FLAG_REMAINING_TIME) {
    if (fits(view, o, 2)) {
      out.remaining_time_s = view.getUint16(o, true);
    }
  }

  return out;
}
